// @ts-check

/**
 * CTA loading spinner.
 *
 * Marks any element carrying `data-cta-spinner` with `data-loading="true"` the
 * moment it is activated, so navigating / redirecting CTAs (checkout, the hero
 * "Start your journey", "Get Cuttea", "Shop now") show a spinner during the
 * unavoidable wait before the next page paints. The state is meant to persist
 * through the page unload; a failsafe timeout and a bfcache handler clear it if
 * the navigation never actually happens (blocked submit, Back button, etc.).
 *
 * Deliberately dependency-free and event-delegated (one listener) — it covers
 * both `<a>` links and native submit `<button>`s without upgrading each one.
 */

const FAILSAFE_MS = 8000;

/** @type {WeakMap<Element, number>} */
const timers = new WeakMap();

/** @param {Element} el */
function startLoading(el) {
  if (el.getAttribute('data-loading') === 'true') return;
  el.setAttribute('data-loading', 'true');
  el.setAttribute('aria-busy', 'true');
  timers.set(el, window.setTimeout(() => stopLoading(el), FAILSAFE_MS));
}

/** @param {Element} el */
function stopLoading(el) {
  el.removeAttribute('data-loading');
  el.removeAttribute('aria-busy');
  const timer = timers.get(el);
  if (timer != null) {
    clearTimeout(timer);
    timers.delete(el);
  }
}

function stopAll() {
  document.querySelectorAll('[data-cta-spinner][data-loading="true"]').forEach(stopLoading);
}

document.addEventListener(
  'click',
  (event) => {
    const origin = event.target;
    if (!(origin instanceof Element)) return;

    const cta = origin.closest('[data-cta-spinner]');
    if (!cta) return;

    // Never spin a disabled control (e.g. checkout with an empty cart).
    if (cta.matches('[disabled], [aria-disabled="true"]')) return;

    // A handler upstream cancelled the action, or this is a modified / non-primary
    // click (new tab, context menu) that leaves the current page in place — a
    // spinner there would be left stranded.
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // Anchors targeting a new browsing context don't navigate this page.
    if (cta instanceof HTMLAnchorElement && cta.target && cta.target !== '_self') return;

    startLoading(cta);

    // Plain same-tab link: a fast navigation can commit before the browser ever
    // paints the spinner frame (the "sometimes no spinner" bug). Take over the
    // navigation and give the spinner one guaranteed painted frame first —
    // double rAF (~2 frames) is imperceptible and still triggers the normal
    // cross-document view transition via location.assign.
    if (
      cta instanceof HTMLAnchorElement &&
      cta.href &&
      !cta.hasAttribute('download') &&
      new URL(cta.href).origin === location.origin
    ) {
      event.preventDefault();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.location.assign(cta.href);
        });
      });
    }
  },
  // Capture: run even if a bubble-phase handler calls stopPropagation.
  { capture: true }
);

// Clean the outgoing snapshot: pagehide fires before the page is frozen into
// the back/forward cache, so a spinner cleared here can never flash on a
// restored page (pageshow-only cleanup runs AFTER the restored frame paints).
window.addEventListener('pagehide', stopAll);

// Belt-and-braces for restores where the snapshot still holds a spinner
// (in-app WebViews with quirky pagehide/persisted behavior). Unconditional:
// on a fresh load it's a no-op — server HTML never ships data-loading.
window.addEventListener('pageshow', stopAll);
